import { useState } from 'react';

/**
 * TruncatedText Component
 * Displays text with "see more" / "see less" functionality
 * 
 * Props:
 * - text: The full text to display
 * - maxLength: Maximum characters before truncation (default: 200)
 * - className: Optional className for the container
 * - children: Optional render function to customize text display (receives text as argument)
 */
const TruncatedText = ({ text, maxLength = 200, className = '', children }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Helper to render text - either using children function or plain text
  const renderText = (textToRender) => {
    if (typeof children === 'function') {
      return children(textToRender);
    }
    return textToRender;
  };

  // Don't truncate if text is short enough
  if (!text || text.length <= maxLength) {
    return <span className={className}>{renderText(text)}</span>;
  }

  // Smart truncation - find the last complete sentence before maxLength
  const getTruncatedText = () => {
    if (text.length <= maxLength) return text;

    // Find the last period, question mark, or exclamation before maxLength
    let truncateAt = maxLength;
    const textUpToLimit = text.substring(0, maxLength);
    
    // Look for sentence endings
    const lastPeriod = textUpToLimit.lastIndexOf('. ');
    const lastQuestion = textUpToLimit.lastIndexOf('? ');
    const lastExclamation = textUpToLimit.lastIndexOf('! ');
    
    // Use the latest sentence ending found
    const sentenceEnd = Math.max(lastPeriod, lastQuestion, lastExclamation);
    
    if (sentenceEnd > maxLength * 0.5) {
      // If we found a sentence ending in the latter half, use it
      truncateAt = sentenceEnd + 1; // +1 to include the punctuation
    } else {
      // Otherwise, try to break at last space to avoid cutting mid-word
      const lastSpace = textUpToLimit.lastIndexOf(' ');
      if (lastSpace > maxLength * 0.7) {
        truncateAt = lastSpace;
      }
    }

    return text.substring(0, truncateAt).trim();
  };

  const truncatedText = getTruncatedText();

  return (
    <span className={className}>
      {isExpanded ? (
        <>
          {renderText(text)}
          <button
            onClick={() => setIsExpanded(false)}
            className="ml-1 text-current opacity-60 hover:opacity-100 hover:underline transition-opacity cursor-pointer bg-transparent border-none p-0 font-inherit"
            style={{ display: 'inline' }}
          >
            ...see less
          </button>
        </>
      ) : (
        <>
          {renderText(truncatedText)}
          <button
            onClick={() => setIsExpanded(true)}
            className="ml-1 text-current opacity-60 hover:opacity-100 hover:underline transition-opacity cursor-pointer bg-transparent border-none p-0 font-inherit"
            style={{ display: 'inline' }}
          >
            ...see more
          </button>
        </>
      )}
    </span>
  );
};

export default TruncatedText;
